// The Timesheet's model (timesheet rules §4): one person × one pay period, from time.day (the sheet),
// time.history and payroll.lockCheck, as the page will draw it (TimesheetView). Pure. To fix lists each
// day's problems with its first fix (the quick edit's, so the row's button opens it with that fix
// chosen), or "Check the day" where the fix is a guess. Weeks and overtime come from the server's
// split, never the page. Minutes only, never pay.
import { localDateOf, zonedParts } from '@flowology/kernel';
import type { DayPartView, TimeChangeView, TimesheetView, ToFixRow } from '@flowology/ui-web/timesheet';
import { checkOf, dayProblems, type DayProblem } from './problems.ts';
import { fixesFor, type BookedVisit, type QuickEditInput } from './quick-edit-model.ts';
import type { PayPeriod, TimeDay, TimeHistoryItem, TimeSheet, TimeViewer } from './timesheet-types.ts';
import { capital, clockOn, dayWords, minutesText, periodWords, shortDay } from './time-words.ts';

export interface TimesheetInput {
  person: { staffId: string; name: string; first: string; pen?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | null };
  sheet: TimeSheet;
  /** The period shown (one of sheet.periods). */
  period: PayPeriod;
  history: TimeHistoryItem[];
  viewer: TimeViewer;
  /** Who locks hours (the bookkeeper), by name; null when no one is set. */
  locker: string | null;
  today: string;
  timeZone: string;
  /** The board's visits by day, for the fixes' booked ends. */
  booked?: Record<string, BookedVisit[]>;
  /** No pay period yet (QA-757): `period` is a week (weekOf); whether this viewer sets payroll up. */
  noPayroll?: { canSetUp: boolean };
  /** The next period isn't open yet (QA-774): `period` is it (notOpenYet); whether this viewer opens it. */
  notOpen?: { canOpen: boolean };
}

const partKind = (e: TimeDay['entries'][number]): DayPartView['kind'] =>
  e.type === 'work'
    ? 'work'
    : e.type === 'drive'
      ? 'drive'
      : e.nonJobCode === 'lunch'
        ? 'lunch'
        : e.nonJobCode === 'supply_house'
          ? 'supply_house'
          : 'shop';

/** A part's span in words: "8:30 a.m. to 3 p.m.", "7 a.m. to no end". */
const spanWords = (e: TimeDay['entries'][number], date: string) =>
  `${clockOn(e.startMinute, date)} to ${e.endMinute === null ? (e.running ? 'now' : 'no end') : clockOn(e.endMinute, date)}`;

/** Who recorded a part for the tech, when it wasn't their own tap (F-20): "Dana for Sam". */
const recordedWords = (e: TimeDay['entries'][number], first: string) =>
  e.recordedBy ? `${e.recordedBy} for ${first}` : null;

/**
 * A day's parts as still TimeBars (the Timesheet's rows): nothing pressable there. A job's part names
 * the customer and the job ("Whitford · #24611"); one the office recorded for the tech says who.
 */
export function dayParts(day: TimeDay, person: { first: string; pen?: DayPartView['pen'] }): DayPartView[] {
  return day.entries.map((e) => {
    const job = e.type !== 'non_job' && e.jobNumber ? `#${e.jobNumber}` : null;
    const by = recordedWords(e, person.first);
    return {
      id: e.id,
      kind: partKind(e),
      startMinute: e.startMinute,
      endMinute: e.endMinute,
      label: e.type === 'work' ? [e.customer ?? 'Work', job].filter(Boolean).join(' · ') : undefined,
      name: [capital(e.name), job ? `job ${job}` : null, spanWords(e, day.date), by]
        .filter(Boolean)
        .join(', '),
      job,
      recordedBy: by,
      state: e.endMinute !== null ? 'done' : e.running ? 'running' : 'noEnd',
      toCheck: checkOf(e),
      paid: e.paid,
      pen: person.pen ?? null,
    };
  });
}

/** The parts the office recorded for the tech, a line each: "Dana for Sam: work at Whitford, 8:30 a.m. to 3 p.m." */
export function recordedLines(day: TimeDay, first: string): string[] {
  return day.entries.flatMap((e) => {
    const by = recordedWords(e, first);
    return by ? [`${by}: ${e.name}, ${spanWords(e, day.date)}`] : [];
  });
}

/** The problem in a To fix row's words: "Work at Whitford never ended". */
export function problemRowWords(p: DayProblem): string {
  if (p.kind === 'notEnded') return 'The day was never ended';
  if (p.kind === 'missed') return `${capital(p.entry.name)} never ended`;
  return `${capital(p.entry.name)}: ${p.words}`;
}

/** One day's To fix rows, each with its first fix; a guess is "Check the day". */
export function toFixRows(
  person: TimesheetInput['person'],
  day: TimeDay,
  o: { today: string; timeZone: string; period: PayPeriod | null; booked?: BookedVisit[] },
): ToFixRow[] {
  if (day.locked) return [];
  const qe: QuickEditInput = {
    staffId: person.staffId,
    day,
    today: o.today,
    nowMinute: 0,
    person,
    booked: o.booked ?? [],
    codes: [],
    period: o.period,
    timeZone: o.timeZone,
  };
  return dayProblems(day, o.today).map((p, i) => {
    // only the day's first problem has its fixes worked out: the dialog opens on it
    const f = i === 0 ? fixesFor(qe, p) : null;
    const chosen = f?.fixes.find((x) => x.id === f.chosen);
    return {
      id: `${day.date}:${i}`,
      staffId: person.staffId,
      name: person.name,
      pen: person.pen ?? null,
      date: day.date,
      day: dayWords(day.date),
      problem: problemRowWords(p),
      fix:
        chosen && chosen.does.kind !== 'typed'
          ? { label: chosen.label, fixId: chosen.id }
          : { label: 'Check the day', fixId: null },
    };
  });
}

/** "Payroll Fri Oct 30", or who locked it and sent it, when. */
export function payLineWords(p: PayPeriod, timeZone: string): string {
  const on = (at: string | null) => (at ? ` on ${dayWords(localDateOf(at, timeZone))}` : '');
  if (p.status === 'sent' || p.status === 'posted')
    return `Locked${p.lockedBy ? ` by ${p.lockedBy}` : ''}${on(p.lockedAt)}, sent to payroll${p.sentBy ? ` by ${p.sentBy}` : ''}${on(p.sentAt)}.`;
  if (p.status === 'locked')
    return `Locked${p.lockedBy ? ` by ${p.lockedBy}` : ''}${on(p.lockedAt)}. Payroll ${dayWords(p.payDate)}.`;
  return `Payroll ${dayWords(p.payDate)}`;
}

/** time.history's items as HistoryItem (kind edit) draws them: was struck, then now; paid before → after. */
export function changeViews(items: TimeHistoryItem[], timeZone: string): TimeChangeView[] {
  return items.map((h) => ({
    id: h.id,
    at: localStamp(h.at, timeZone),
    who: h.by ?? 'Someone',
    when: h.atWords,
    day: dayWords(h.date),
    diff: h.changes,
    why: h.reason,
    paid:
      h.paidBefore !== null && h.paidAfter !== null
        ? `${minutesText(h.paidBefore)} → ${minutesText(h.paidAfter)} paid`
        : h.paidChange
          ? `${h.paidChange > 0 ? '+' : ''}${minutesText(h.paidChange)} paid`
          : null,
  }));
}

/** An instant on the company's clock, "2026-10-23T11:21". */
export function localStamp(at: string, timeZone: string): string {
  const p = zonedParts(new Date(at), timeZone);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${localDateOf(at, timeZone)}T${two(p.hour)}:${two(p.minute)}`;
}

/**
 * The one quiet line Hours and the Timesheet show until payroll is set up (QA-757): a link to set it
 * up for those who may (payroll.prepare), plain words for the others.
 */
export function noPayrollLine(canSetUp: boolean): { words: string; link: string | null } {
  return canSetUp
    ? { words: 'Pay periods and locking start once payroll is set up.', link: 'Set up payroll ›' }
    : { words: 'Pay periods and locking start once the owner sets up payroll.', link: null };
}

/**
 * The quiet line where the pay line goes while the period shown isn't open yet (QA-774): its hours show
 * anyway, it says it can't lock until it's opened and who opens it, and links to Payroll for them.
 */
export function notOpenLine(
  period: Pick<PayPeriod, 'startDate' | 'endDate'>,
  canOpen: boolean,
): { words: string; link: string | null } {
  const pw = periodWords(period.startDate, period.endDate);
  return canOpen
    ? {
        words: `${pw} isn’t open yet, so it can’t be locked. You open it on Payroll.`,
        link: 'Open it on Payroll ›',
      }
    : {
        words: `${pw} isn’t open yet, so it can’t be locked. The owner or the bookkeeper opens it on Payroll.`,
        link: null,
      };
}

/**
 * The quiet line over the days when the period shown runs past the hours imported from the earlier
 * system (QA-760): the days after them have only the time recorded here, so an empty week says why.
 */
export function importLine(importedThrough: string | null | undefined, periodEnd: string): string | null {
  return importedThrough && periodEnd > importedThrough
    ? `Hours imported from the earlier system go up to ${dayWords(importedThrough)}. Later days show only the time recorded here.`
    : null;
}

/** Written for the viewer (rules §4): who locks the period, and where. */
export function lockLineWords(viewer: TimeViewer, locker: string | null, period: string): string {
  const tail = `on Hours once nothing is left to fix.`;
  if (viewer === 'bookkeeper') return `You lock ${period} ${tail}`;
  if (viewer === 'owner')
    return locker ? `You or ${locker} lock ${period} ${tail}` : `You lock ${period} ${tail}`;
  return `${locker ?? 'The owner'} locks ${period} ${tail}`;
}

export function timesheetView(input: TimesheetInput): TimesheetView {
  const { person, sheet, period } = input;
  const pw = periodWords(period.startDate, period.endDate);
  const days = sheet.days.filter((d) => d.date >= period.startDate && d.date <= period.endDate);
  const toFix = days.flatMap((d) =>
    toFixRows(person, d, {
      today: input.today,
      timeZone: input.timeZone,
      period,
      booked: input.booked?.[d.date],
    }),
  );
  const t = sheet.totals;
  const sum = (f: (d: TimeDay) => number) => days.reduce((n, d) => n + f(d), 0);
  const lunch = sum((d) =>
    d.entries.reduce(
      (n, e) => n + (e.nonJobCode === 'lunch' && !e.paid && e.endMinute !== null ? e.minutes : 0),
      0,
    ),
  );
  const locked = period.status !== 'open';
  const noPayroll = input.noPayroll
    ? noPayrollLine(input.noPayroll.canSetUp)
    : input.notOpen
      ? notOpenLine(period, input.notOpen.canOpen)
      : null;
  return {
    crumb: `Hours › ${person.name}`,
    title: `${person.name} · ${pw}`,
    payLine: noPayroll ? '' : payLineWords(period, input.timeZone),
    noPayroll,
    importNote: importLine(sheet.importedThrough, period.endDate),
    facts: [
      { label: 'Paid', value: minutesText(t.paid) },
      { label: 'At time and a half', value: minutesText(t.overtime) },
      ...(t.double ? [{ label: 'At double', value: minutesText(t.double) }] : []),
      { label: 'To fix', value: String(toFix.length), ...(toFix.length ? { tone: 'bad' as const } : {}) },
    ],
    toFix,
    days: days.map((d) => ({
      date: d.date,
      day: dayWords(d.date),
      bad: !d.locked && dayProblems(d, input.today).length > 0,
      parts: dayParts(d, person),
      ...(d.entries.some((x) => x.recordedBy) ? { recorded: recordedLines(d, person.first) } : {}),
      paid: minutesText(d.split.paid),
      past8h: d.split.past8h ? `${minutesText(d.split.past8h)} past 8 h` : null,
    })),
    weeks: sheet.weeks.map((w) => ({
      label:
        w.from < period.startDate && !input.noPayroll
          ? `Week of ${shortDay(w.from)} (it starts in the last period)`
          : `Week of ${shortDay(w.from)}`,
      to: w.to,
      paid: minutesText(w.paid),
      past40h: w.past40h ? `${minutesText(w.past40h)} past 40 h` : null,
    })),
    breakdown: [
      { label: 'Work', value: minutesText(sum((d) => d.totals.work)) },
      { label: 'Drive', value: minutesText(sum((d) => d.totals.drive)) },
      { label: 'Shop and supply house', value: minutesText(sum((d) => d.totals.nonJob)) },
      { label: 'Lunch (not paid)', value: minutesText(lunch) },
      { label: 'Past 40 h in a week', value: minutesText(t.past40h) },
    ],
    changes: changeViews(input.history, input.timeZone),
    // with no pay period, the quiet line at the top says it all: nothing locks yet
    lockLine: noPayroll
      ? ''
      : locked
        ? payLineWords(period, input.timeZone)
        : lockLineWords(input.viewer, input.locker, pw),
  };
}
